import { useNotification } from "@context/NotificationContext";
import { generatePrefixedNotifyList } from "@features/vessel-call/vessel-call-status/PortServices/preview/utils";
import { useAuthStore } from "@persisted-state/auth/auth.store";
import type { MessagePayload } from "@services/message-service/types";
import { sendMessage } from "@services/messageApi";
import { useMutation } from "@tanstack/react-query";
import { runTransaction } from "@telemetry/runTransaction";
import { t } from "i18next";

interface UseSendMessageProps {
  newMessage: string;
  entityType: string;
  entityId: string;
  secondRelatedEntityId?: string;
  notifyList?: string[];
  setShouldRefetch: (value: boolean) => void;
  setNewMessage: (arg: string) => void;
}
const LANG_BASE = "vessel-call.port-services";

export interface UseSendMessageResult {
  onSubmit: () => Promise<void>;
}

export const useSendMessage = (props: UseSendMessageProps): UseSendMessageResult => {
  const {
    newMessage,
    entityId,
    entityType,
    secondRelatedEntityId,
    notifyList,
    setNewMessage,
    setShouldRefetch,
  } = props;
  const { notify } = useNotification();
  const { user } = useAuthStore();

  const sendMessageMutation = useMutation<unknown, unknown, MessagePayload>({
    mutationFn: (payload: MessagePayload) => {
      return runTransaction(
        {
          module: "messaging",
          screen: "MessageBox",
          action: "send_message",
          entityType: "message",
        },
        (transactionId) => sendMessage({ payload, ...(transactionId ? { transactionId } : {}) }),
      );
    },
    onSuccess: () => {
      setShouldRefetch?.(true);
      setNewMessage("");
    },
    onError: (error) => {
      notify({
        title: t(`${LANG_BASE}.notifications.service-status-change-error-title`),
        description: t(`${LANG_BASE}.notifications.service-status-change-error-description`),
        type: "error",
      });
      console.error("Error submitting port service request:", error);
    },
  });

  const onSubmit = async (): Promise<void> => {
    if (!newMessage.trim()) return;
    let newMessagePayload: MessagePayload = {
      entityId,
      sender: user?.preferredUsername || "",
      entityType,
      message: newMessage,
      sentAt: new Date().toISOString(),
      secondRelatedEntityId,
    };
    if (notifyList) {
      const notificationFor = generatePrefixedNotifyList("MESSAGE_SENT", notifyList);

      newMessagePayload = {
        ...newMessagePayload,
        notificationFor,
      };
    }

    sendMessageMutation.mutate(newMessagePayload);
  };
  return { onSubmit };
};
